import React from "react";
import { Routes, Route } from "react-router-dom";
import { useEffect, useState } from "react";
import Campaign from "@pages/public/Campaign";
import { useSelector } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PageRoutes from "@routes/index";
import { selectHTTPStatus, setStatus } from "@slices/httpStatusSlice";
import { useAppDispatch } from "@store/hooks";
import ThemeProvider from "@theme/ThemeProvider";
import dialogContext, { DialogType } from "@utilities/contexts/dialogContext";
import AlertDialog from "@components/AlertDialog";
import { cleanupOnTabOrBrowserClose } from "@utilities/cleanupOnTabOrBrowserClose";
import "./App.css";
import Loader from "@components/Loader";
import useUtmTracking from "./hooks/useUtmTracking";

interface AppProps {
    campaignData: {
        success: boolean;
        total_count: number;
        current_page_number: number;
        item_per_page: number;
        message: string;
        data: {
            title: string;
            video_link: string;
            video_des: string;
            payment_description: string;
            slug: string;
            description: string;
            image_url_2: string;
            amount_goal: number;
            raised_amount_in_bdt: number;
            start_date: string;
            accept: boolean;
            modal: boolean;
            offline: boolean;
            amount: boolean;
            payment_method:boolean;
            autopay: boolean
        };
    } | null;
}

const App: React.FC<AppProps> = ({ campaignData }) => {
    useUtmTracking();
    const httpStatus = useSelector(selectHTTPStatus);
    const dispatch = useAppDispatch();

    const [dialogType, setDialogType] = useState<number>(DialogType.MESSAGE);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [title, setTitle] = useState<string>("");
    const [content, setContent] = useState<string>("");
    // const [color, setColor] = useState<"error" | "success" | "info" | "warning" | "inherit" | "primary" | "secondary">("primary");
    type ColorType = "error" | "success" | "info" | "warning" | "inherit" | "primary" | "secondary";

    const [color, setColor] = useState<ColorType>("primary");
    const [verificationText, setVerificationText] = useState<string>("");
    const [okButtonTitle, setOkButtonTitle] = useState<string>("");
    const [cancelButtonTitle, setCancelButtonTitle] = useState<string>("");
    const [onClickOk, setOnClickOk] = useState<() => void>(() => {});
    const [onClickCancel, setOnClickCancel] = useState<() => void>(() => {});

    // const statusCodeToHandle: any = {
    //     401: {
    //         title: "Session Expired!",
    //         message: "You have been logged out. Please Sign in Again",
    //     },
    //     500: {
    //         title: "Error!",
    //         message: "Please Try Again Later",
    //     },
    // };
    const statusCodeToHandle: { [key: number]: { title: string; message: string } } = {
        401: {
            title: "Session Expired!",
            message: "You have been logged out. Please Sign in Again",
        },
        500: {
            title: "Error!",
            message: "Please Try Again Later",
        },
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
            cleanupOnTabOrBrowserClose();
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    useEffect(() => {
        if (Object.keys(statusCodeToHandle).includes(httpStatus.code.toString())) {
            const onClose = () => {
                dispatch(
                    setStatus({
                        code: 0,
                        message: "",
                    })
                );
                setShowDialog(false);
            };

            setTitle(statusCodeToHandle[httpStatus.code].title);
            setContent(statusCodeToHandle[httpStatus.code].message);
            setDialogType(DialogType.MESSAGE);
            setOnClickOk(() => onClose);
            setOnClickCancel(() => onClose);
            setShowDialog(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [httpStatus]);

    return (
        <dialogContext.Provider
            value={{
                dialogType,
                setDialogType,
                showDialog,
                setShowDialog,
                title,
                setTitle,
                content,
                setContent,
                color,
                setColor,
                verificationText,
                setVerificationText,
                okButtonTitle,
                setOkButtonTitle,
                cancelButtonTitle,
                setCancelButtonTitle,
                onClickOk,
                setOnClickOk,
                onClickCancel,
                setOnClickCancel,
            }}
        >
            <div className="App">
                <ThemeProvider>
                    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <AlertDialog />
                            <Routes>
                                <Route path="/campaign/:slug" element={<Campaign campaignData={campaignData} />} />
                            </Routes>
                            <PageRoutes />
                            <Loader />
                        </LocalizationProvider>
                    </GoogleOAuthProvider>
                </ThemeProvider>
            </div>
        </dialogContext.Provider>
    );
};

export default App;
